import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { IonCol, IonRow } from '@ionic/angular/standalone';
import { tap } from 'rxjs';
import {
  AdlSummaryIDO,
  AvailableDay,
  StartEnd,
  TableAccessPointIDO,
  TableAvailability,
} from 'src/app/core/models/admin';
import {
  AdlDetail,
  AdlDetails,
  GetAllAdlDetails,
} from 'src/app/core/models/admin/adl.model';
import { AdlService } from 'src/app/core/services/admin/adl/adl.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { BANNER_TYPE } from '../../banner/banner.constants';
import { EditAccessPointComponent } from '../../shared/modal/edit-access-point/edit-access-point.component';
import { BUTTON_CLICK } from '../../shared/modal/exit-modal/exit-modal.constants';
import {
  EDIT_TEXT,
  IMAGE_SRC,
  STATUS_OPTIONS,
} from '../../shared/shared-constants';
import { SharedModule } from '../../shared/shared.module';
import { AdminModule } from '../admin.module';
import {
  ColumnIds,
  ColumnText,
  DISPLAY_TEXT,
  EXCEL_FILE_NAME,
  ROUTE_TEXT,
  STATUS,
} from './access-point.constants';
import { AdlSummaryComponent } from './adl-summary/adl-summary.component';

@Component({
  selector: 'app-access-point',
  imports: [IonRow, IonCol, SharedModule, AdminModule, MatTableModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './access-point.component.html',
  styleUrls: ['./access-point.component.scss'],
})
export class AccessPointComponent implements OnInit {
  private readonly destroy = inject(DestroyRef);
  private readonly userService = inject(UserService);
  private readonly commonUtils = inject(CommonUtils);
  private readonly adlService = inject(AdlService);
  private readonly dialog = inject(MatDialog);

  //Display Constants
  readonly CONSTANTS = DISPLAY_TEXT;
  readonly IDS = Object.values(ColumnIds);
  readonly COLUMNTEXT = ColumnText;
  readonly IMAGES = IMAGE_SRC;
  readonly ROUTE_TEXT = ROUTE_TEXT;
  readonly EDIT_TEXT = EDIT_TEXT;

  dataSource = new MatTableDataSource<TableAccessPointIDO>();

  currentLocation!: string;
  accessPointDetail!: AdlDetails[];
  availableDays: AvailableDay[][] = [];
  closedDays: StartEnd[] = [];
  expandedTable: TableAccessPointIDO[] = [];

  ngOnInit(): void {
    this.userService
      .getCurrentUser$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        tap((user) => {
          this.currentLocation = user.location;
          this.getADlDetails(user.location);
        }),
      )
      .subscribe();
  }

  convertAvailability(availability: string): TableAvailability[] {
    return [
      { day: 'Su', value: this.checkOpenDay('01', availability).length > 0 },
      { day: 'Mo', value: this.checkOpenDay('02', availability).length > 0 },
      { day: 'Tu', value: this.checkOpenDay('03', availability).length > 0 },
      { day: 'We', value: this.checkOpenDay('04', availability).length > 0 },
      { day: 'Th', value: this.checkOpenDay('05', availability).length > 0 },
      { day: 'Fr', value: this.checkOpenDay('06', availability).length > 0 },
      { day: 'Sa', value: this.checkOpenDay('07', availability).length > 0 },
    ];
  }

  checkOpenDay(day: string, availableString: string) {
    const reg1 = /,10,(.){17},/;
    //after the number there is a date that is always 10 characters long
    const reg2 = /,(.){10}/;
    const reg3 = new RegExp(reg1.source + day + reg2.source);
    const mainRegex = RegExp(reg3).exec(availableString);
    return mainRegex ? mainRegex[0].toString().split(',') : [];
  }

  exportExcel(): void {
    const filteredData = this.dataSource.filteredData.map((value) => {
      return {
        loop: value.loop,
        name: value.name,
        uapPublicId: value.uapPublicId,
        city: value.city,
        phoneNumber: value.phoneNumber,
        uapStatus: value.uapStatus,
      };
    });

    this.commonUtils.exportExcel(
      Object.values(ColumnText),
      EXCEL_FILE_NAME,
      filteredData,
    );
  }

  getRouteIdText(
    data: TableAccessPointIDO,
    accessPointDetail: AdlDetails[],
  ): string {
    if (!data.displayLoop) {
      return '';
    } else {
      const validAdls = this.commonUtils.filterOutInvalidAccessPoints(
        accessPointDetail[data.index]?.adls,
      ) as AdlDetail[];
      const number = ROUTE_TEXT.replace('%n', data?.loop);
      if (accessPointDetail) {
        return number.replace('%s', validAdls.length.toString());
      } else {
        return '';
      }
    }
  }

  editAccessPoint(index: number) {
    const adlLoop = this.dataSource.data.find(
      (value) => value.index === index,
    )?.loop;
    const accessDetail = this.accessPointDetail.find(
      (value) => value.loop === adlLoop,
    );

    this.dialog
      .open(EditAccessPointComponent, {
        minWidth: '56.25rem',
        minHeight: '36.125rem',
        disableClose: true,
        data: {
          data: this.commonUtils.filterOutInvalidAccessPoints(
            accessDetail?.adls || [],
          ),
          name: accessDetail?.loop,
        },
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((value) => {
        if (value === BUTTON_CLICK.ACCEPT) {
          this.clearSearch();
          this.getADlDetails(this.currentLocation);
        }
      });
  }

  openAdlSummary(loop: string, uapPublicId: string, id: number): void {
    const filteredLoop = this.accessPointDetail.filter((data) => {
      if (data.loop === loop) {
        return data;
      } else return null;
    });
    const adl = filteredLoop[0].adls.filter(
      (data) => data.uapPublicId === uapPublicId && data.id === id.toString(),
    );

    this.dialog.open(AdlSummaryComponent, {
      disableClose: true,
      data: this.divideDataPerCard(adl[0]),
      width: '59.375rem',
    });
  }

  divideDataPerCard(adl: AdlDetail): AdlSummaryIDO {
    this.availableDays = [];
    this.closedDays = [];
    this.getOpenDays(adl);
    this.getClosedDays(adl);
    return {
      firstCardData: {
        accessPointName: adl.name,
        uapPublicId: adl.uapPublicId,
        phoneNumber: this.convertPhoneNumber(adl.phoneNumber),
        streetName: `${adl.streetNumber ? adl.streetNumber : ''} ${
          adl.streetName ? adl.streetName : ''
        } ${adl.streetType ? adl.streetType : ''} `,
        city: adl.city,
        province: adl.province ?? '',
        postalCode: adl.postalCode,
        country: adl.country ?? '',
        latLong:
          adl?.latitude && adl?.longitude
            ? `${Number.parseFloat(adl.latitude.toString()).toPrecision(
                6,
              )} , ${Number.parseFloat(adl.longitude.toString()).toPrecision(
                6,
              )}`
            : '-',
        proximityRadius: adl?.proximityRadius
          ? Number.parseFloat(adl.proximityRadius.toString()).toPrecision(7)
          : '-',
      },
      secondCardData: {
        availableDays: this.availableDays,
        closeDays: this.closedDays,
      },
      thirdCardData: Object.values(adl.adlIndicators).map((value) => {
        if (value === 'Yes') {
          return true;
        } else {
          return false;
        }
      }),
    };
  }

  getOpenDays(adl: AdlDetail) {
    // Sunday details
    this.getDaysDetails(/,10,(.){17},01,(.){10}/g, adl);
    // Monday details
    this.getDaysDetails(/,10,(.){17},02,(.){10}/g, adl);
    // Tuesday details
    this.getDaysDetails(/,10,(.){17},03,(.){10}/g, adl);
    // Wednesday details
    this.getDaysDetails(/,10,(.){17},04,(.){10}/g, adl);
    // Thursday details
    this.getDaysDetails(/,10,(.){17},05,(.){10}/g, adl);
    // Friday details
    this.getDaysDetails(/,10,(.){17},06,(.){10}/g, adl);
    // Saturday details
    this.getDaysDetails(/,10,(.){17},07,(.){10}/g, adl);
  }

  getClosedDays(adl: AdlDetail) {
    this.closedDays = [];
    if (adl.daysClosed) {
      const matchedValues = [
        ...adl.daysClosed.matchAll(new RegExp(/.{10},.{10}/g)),
      ];
      if (matchedValues.length > 0) {
        const value = matchedValues[0].toString().split(',');
        this.closedDays.push({
          start: value[0]?.trim(),
          end: value[1]?.trim(),
        });
      }
    }
  }

  getDaysDetails(reg: RegExp, adl: AdlDetail) {
    const mainRegex = [...adl.hoursOfOperation.matchAll(reg)];
    const timesOfDay: AvailableDay[] = [];
    if (mainRegex.length > 0) {
      mainRegex.forEach((regValue, index) => {
        const value = regValue[0].toString().split(',');
        timesOfDay.push({
          status: index === 0 ? STATUS_OPTIONS.ACTIVE : '',
          times: { start: value[2], end: value[3] },
          effectiveDate: value[5],
        });
      });
    } else {
      timesOfDay.push({
        status: STATUS_OPTIONS.DEACTIVATED,
        times: { start: '', end: '' },
        effectiveDate: '',
      });
    }
    this.availableDays.push(timesOfDay);
  }

  search(searchValue: string): void {
    this.dataSource.filter = searchValue;
    let currentIndex = -1;
    this.dataSource.filteredData.forEach((data) => {
      if (currentIndex === data.index) {
        data.displayLoop = false;
      } else {
        data.displayLoop = true;
        currentIndex = data.index;
      }
    });
  }

  getRowSpan(data: TableAccessPointIDO) {
    let returnValue = 0;
    if (data.displayLoop) {
      returnValue = this.dataSource.filteredData.filter(
        (filteredData) => filteredData.index === data.index,
      ).length;
    }
    return returnValue;
  }

  clearSearch() {
    this.dataSource.filter = '';
    let currentIndex = -1;
    this.dataSource.filteredData.forEach((data) => {
      if (currentIndex === data.index) {
        data.displayLoop = false;
      } else {
        data.displayLoop = true;
        currentIndex = data.index;
      }
    });
  }

  private getADlDetails(locationCode: string) {
    if (locationCode && locationCode !== '') {
      this.adlService
        .getAllADlDetails(locationCode)
        .pipe(
          takeUntilDestroyed(this.destroy),
          tap((data: GetAllAdlDetails) => {
            this.expandedTable = [];
            this.accessPointDetail = data?.response?.adlDetails;
            let adlDetails = this.accessPointDetail
              .slice()
              .sort((a, b) => a.loop.localeCompare(b.loop));
            adlDetails = adlDetails.map((adlDetail: AdlDetails) => {
              //IF ONLY value is a terminated one, set the adls to null so it will still appear on the table and you can edit
              if (
                adlDetail.adls.length === 1 &&
                this.commonUtils.convertStatus(adlDetail.adls[0].uapStatus) ===
                  STATUS.TERMINATED
              ) {
                return {
                  ...adlDetail,
                  adls: [{} as AdlDetail],
                };
              } else {
                return adlDetail;
              }
            });
            adlDetails?.forEach((adlDetail, i) => {
              const validVals =
                (this.commonUtils.filterOutInvalidAccessPoints(
                  adlDetail.adls,
                ) as AdlDetail[]) ?? [];
              validVals?.forEach((data2, k) => {
                this.expandedTable.push({
                  index: i,
                  loop: adlDetail.loop,
                  displayLoop: k === 0,
                  name: data2.name,
                  id: data2.id,
                  uapPublicId: data2?.uapPublicId,
                  hoursOfOperation: this.convertAvailability(
                    data2.hoursOfOperation,
                  ),
                  city: data2.city,
                  phoneNumber: this.convertPhoneNumber(data2.phoneNumber),
                  uapStatus: this.commonUtils.convertStatus(data2.uapStatus),
                });
              });
            });
            this.dataSource.data = this.expandedTable;
            this.dataSource.filterPredicate = this.createFilter();
            if (this.dataSource.data.length === 0) {
              this.commonUtils.updateBannerType(BANNER_TYPE.NO_DATA_AVAILABLE);
            }
            this.commonUtils.lockUpdateLoadingModal(false);
            this.commonUtils.updateLoadingModal(false);
          }),
        )
        .subscribe();
    }
  }

  private convertPhoneNumber(phoneNumber: string): string {
    const convertedNumber: string[] = phoneNumber?.split(',');
    return convertedNumber ? convertedNumber[2] : '';
  }

  private createFilter(): (
    data: TableAccessPointIDO,
    filter: string,
  ) => boolean {
    let matchedRowIndex = -1;
    return (data: TableAccessPointIDO, filter: string) => {
      const filterData = filter.toString().toLowerCase();
      let returnRow = false;
      const currentIndex = data.index;
      Object.entries(data).forEach((dataValue: string[]) => {
        if (currentIndex === matchedRowIndex) {
          returnRow = true;
        } else {
          matchedRowIndex = -1;
        }
        switch (true) {
          case returnRow:
          case dataValue[0] === ColumnIds.AVAILABILITY:
          case dataValue[0] === ColumnIds.STATUS_COL:
          case dataValue[0] === 'index':
          case dataValue[0] === 'displayLoop':
          case dataValue[0] === 'id':
            return;
          case dataValue[0] === ColumnIds.ROUTE:
            returnRow = dataValue[1]
              ?.toString()
              .toLowerCase()
              .includes(filterData);
            if (returnRow) {
              matchedRowIndex = currentIndex;
            }
            return;
          default:
            returnRow = dataValue[1]
              ?.toString()
              .toLowerCase()
              .includes(filterData);
            break;
        }
      });
      return returnRow;
    };
  }
}
